/**
 * supernovaMeta — the four Supernova tokens as the collection describes them:
 * id, name, lore, and the bundled artwork. One record per ERC-1155 token id.
 *
 * THIS IS COLLECTION METADATA, NOT CHAIN STATE. Nothing here says whether a
 * member owns, stakes, or has ever held any of these — those are reads against
 * the member's own wallet (useSupernovaChain) and never come from this file.
 * A record existing here means the token exists in the collection, nothing more.
 *
 * PROVENANCE. `description` is the collection's own lore text and `cid` is the
 * IPFS CID from each token's metadata — the same CID the bundled PNG was
 * downloaded from, recorded so the art can be re-fetched and checked against
 * the source without going back through a block explorer. The images are
 * BUNDLED rather than hot-linked: no third-party request on render, and no
 * blank tile when a public gateway is having a bad day. Full-resolution
 * 1024×1024 originals are kept alongside the display copies in
 * `src/assets/supernova/masters/` and are deliberately not imported — bundling
 * ~8MB of art to draw a 42px orb would be a waste of every member's bandwidth.
 *
 * ids and names are duplicated from supernovaContracts.SUPERNOVA_TOKENS by
 * design: that module is the chain-facts source and stays free of asset imports
 * so it remains cheap to import in tests. supernovaMeta.test.js asserts the two
 * agree exactly, so neither can drift without a red test.
 *
 * COMPLIANCE: the lore is the collection's flavour text. Nothing in this file is
 * a return, a yield, a price, a projection, or a statement about what holding or
 * staking a token will earn — and no copy added here may become one.
 */

import celestialFlareArt from "../assets/supernova/celestial-flare.png";
import cosmicPulseArt from "../assets/supernova/cosmic-pulse.png";
import galacticEmberArt from "../assets/supernova/galactic-ember.png";
import stellarNovaArt from "../assets/supernova/stellar-nova.png";

/**
 * @typedef {Object} SupernovaTokenMeta
 * @property {number} id           ERC-1155 token id (7–10)
 * @property {string} name         the collection's name for the token
 * @property {string} description  the collection's lore text, verbatim
 * @property {string} cid          IPFS CID the artwork came from
 * @property {string} art          bundled display artwork (128×128)
 */

/** The four tokens, in display order. */
export const SUPERNOVA_META = [
  {
    id: 7,
    name: "Celestial Flare",
    description:
      "This mysterious door hovers in the cosmic void, beckoning you to explore the hidden corners of the multiverse.",
    cid: "bafybeihbrtogmta5jrgbg524nsdbpvdvwktbk62ha5m3ewantss3hozfhm",
    art: celestialFlareArt,
  },
  {
    id: 8,
    name: "Cosmic Pulse",
    description:
      "Unlock the door that leads to uncharted territories in the far reaches of the universe.",
    cid: "bafybeiarphjs66t42naqjbhpmbrqseq2elftz7uoc2lslzaywxk5p33cnq",
    art: cosmicPulseArt,
  },
  {
    id: 9,
    name: "Galactic Ember",
    description:
      "Step through this ancient door, suspended in the vastness of space, where the laws of physics bend to your will.",
    cid: "bafybeihp3rgvmbjqiabqd5hj3crmuygfgk2kal32xu6svvw4lop5u3mp64",
    art: galacticEmberArt,
  },
  {
    id: 10,
    name: "Stellar Nova",
    description:
      "This enigmatic door floats silently in space, guarding the entrance to a universe filled with celestial marvels.",
    cid: "bafybeie454wyhl7zim5tky4t3k6zyoabprdix5tl2wifypjkqn63qpvtca",
    art: stellarNovaArt,
  },
];

const BY_ID = new Map(SUPERNOVA_META.map((token) => [token.id, token]));

/**
 * The record for a token id, or null for an id the collection doesn't have.
 * Null rather than a placeholder: a caller must decide what to draw for a token
 * we know nothing about, rather than silently render someone else's art.
 *
 * @param {number} tokenId
 * @returns {SupernovaTokenMeta|null}
 */
export function supernovaMeta(tokenId) {
  return BY_ID.get(tokenId) || null;
}

/** The bundled artwork for a token id, or null if we have none for it. */
export function supernovaArt(tokenId) {
  return BY_ID.get(tokenId)?.art || null;
}

export default SUPERNOVA_META;
